import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Sheet,
  Typography,
} from "@mui/joy";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formOpenSelector, setFormOpen } from "../../reducers/rhapsody";
import {
  useDeletePasskeyMutation,
  usePasskeyRegisterOptionsMutation,
  usePasskeyRegisterVerifyMutation,
  usePasskeysQuery,
} from "../../redux/passkey";
import {
  createPasskeyCredential,
  defaultPasskeyLabel,
  isPasskeySupported,
  passkeyErrorMessage,
} from "../../webAuthn";
import DialogClose from "../dialogClose/dialogClose";
import {
  DialogHeader,
  FieldLabel,
  dialogPaperSx,
  groupSheetSx,
  pillButtonSx,
  tileColors,
} from "../formKit/formKit";

/**
 *
 * @returns {ReactElement} Passkeys management dialog
 */
export function Passkeys() {
  const open = useSelector(formOpenSelector("passkeys"));
  const dispatch = useDispatch();
  const { data: passkeys = [], isFetching } = usePasskeysQuery(undefined, {
    skip: !open,
  });
  const [registerOptions] = usePasskeyRegisterOptionsMutation();
  const [registerVerify] = usePasskeyRegisterVerifyMutation();
  const [deletePasskey] = useDeletePasskeyMutation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onClose = () => {
    setError("");
    dispatch(setFormOpen({ formID: "passkeys", isOpen: false }));
  };

  const addPasskey = async () => {
    if (!isPasskeySupported()) {
      setError("Passkeys are not supported on this device or browser.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const { flowId, options } = await registerOptions().unwrap();
      const credential = await createPasskeyCredential(options);
      await registerVerify({
        flowId,
        label: defaultPasskeyLabel(),
        credential,
      }).unwrap();
    } catch (e) {
      setError(passkeyErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogClose
      open={open}
      onClose={onClose}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle>
        <DialogHeader
          title="Passkeys"
          subtitle="Sign in faster and more securely with Face ID, Touch ID, or a security key — no password."
        />
      </DialogTitle>
      <DialogContent>
        <Sheet variant="soft" sx={groupSheetSx}>
          {isFetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size="sm" />
            </Box>
          ) : passkeys.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography level="body2">
                You don&apos;t have any passkeys yet.
              </Typography>
            </Box>
          ) : (
            passkeys.map((passkey) => (
              <Box
                key={passkey.credentialID}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  pl: 1,
                  pr: 1.5,
                  minHeight: 44,
                }}
              >
                <FieldLabel
                  icon="fa-solid fa-fingerprint"
                  color={tileColors.blue}
                  label={passkey.label?.trim() ? passkey.label : "Passkey"}
                />
                <IconButton
                  size="sm"
                  variant="plain"
                  color="danger"
                  onClick={() => {
                    setError("");
                    deletePasskey(passkey.credentialID)
                      .unwrap()
                      .catch((e) => setError(passkeyErrorMessage(e)));
                  }}
                >
                  <i className="fa-solid fa-trash"></i>
                </IconButton>
              </Box>
            ))
          )}
        </Sheet>
        {error ? (
          <Alert color="danger" variant="soft" sx={{ mt: 1 }}>
            {error}
          </Alert>
        ) : (
          []
        )}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button
            disabled={busy}
            onClick={addPasskey}
            sx={pillButtonSx}
            startDecorator={
              busy ? (
                <CircularProgress size="sm" />
              ) : (
                <i className="fa-solid fa-fingerprint"></i>
              )
            }
          >
            Add a passkey
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="plain"
          color="neutral"
          sx={pillButtonSx}
        >
          Close
        </Button>
      </DialogActions>
    </DialogClose>
  );
}
